// @spec VUL-STR-014, VUL-STR-058, VUL-STR-059, VUL-STR-057
'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  NotificationConfigCard,
  NotificationRulesList,
  SeverityMappingCard,
} from '@/features/settings/vulnerabilities';
import {
  CustomFieldFormDialog,
  CustomFieldsList,
  DeleteCustomFieldDialog,
} from '@/features/vulnerabilities/admin/custom-fields';
import type { VulnerabilityCustomField } from '@/features/vulnerabilities/admin/custom-fields';
import { SlaConfigCard } from '@/features/vulnerabilities/admin/sla-config';
import {
  WorkflowConfigForm,
  WorkflowPreview,
} from '@/features/vulnerabilities/admin/workflow-config';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/vulnerability-settings')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.vulnerabilitySettings'),
  }),
  component: VulnerabilitySettingsPage,
});

/**
 * Organization vulnerability settings page for managing severity mapping,
 * custom field configuration, notification preferences, and notification rules.
 */
function VulnerabilitySettingsPage() {
  const { t } = useTranslation('settings');
  const { isAdmin } = useOrgRole();

  // Custom field dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editField, setEditField] = useState<VulnerabilityCustomField | null>(null);
  const [deleteField, setDeleteField] = useState<VulnerabilityCustomField | null>(null);

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('vulnerabilitySettingsPage.title')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('vulnerabilitySettingsPage.description')}</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('vulnerabilitySettingsPage.linkedCardTitle')}
            </CardTitle>
            <CardDescription>
              {t('vulnerabilitySettingsPage.linkedCardDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link
                to="/settings/configration"
                search={{ aspect: 'vulnerabilities', section: 'attributes' }}
              >
                {t('vulnerabilitySettingsPage.openCustomFields')}
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link
                to="/settings/configration"
                search={{ aspect: 'vulnerabilities', section: 'guidelines' }}
              >
                {t('vulnerabilitySettingsPage.openGuidelines')}
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Tabs defaultValue="workflow" className="space-y-6">
          <TabsList>
            <TabsTrigger value="workflow">{t('vulnerabilitySettingsPage.workflowTab')}</TabsTrigger>
            <TabsTrigger value="fields">{t('vulnerabilitySettingsPage.fieldsTab')}</TabsTrigger>
            <TabsTrigger value="governance">
              {t('vulnerabilitySettingsPage.governanceTab')}
            </TabsTrigger>
            <TabsTrigger value="integrations">
              {t('vulnerabilitySettingsPage.integrationsTab')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="workflow" className="space-y-6">
            <SeverityMappingCard isAdmin={isAdmin} />
            <SlaConfigCard isAdmin={isAdmin} />
            <WorkflowConfigForm isAdmin={isAdmin} />
            <WorkflowPreview />
          </TabsContent>

          <TabsContent value="fields" className="space-y-6">
            <CustomFieldsList
              isAdmin={isAdmin}
              onCreateClick={() => {
                setCreateDialogOpen(true);
              }}
              onEditClick={(field) => {
                setEditField(field);
              }}
              onDeleteClick={(field) => {
                setDeleteField(field);
              }}
            />
          </TabsContent>

          <TabsContent value="governance" className="space-y-6">
            <NotificationConfigCard isAdmin={isAdmin} />
            <NotificationRulesList isAdmin={isAdmin} />
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('vulnerabilitySettingsPage.scannerIntegrationsTitle')}</CardTitle>
                <CardDescription>
                  {t('vulnerabilitySettingsPage.scannerIntegrationsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {t('vulnerabilitySettingsPage.scannerGovernanceDescription')}
                </p>
                <Button asChild>
                  <Link to="/settings/scanner-integrations">
                    {t('vulnerabilitySettingsPage.openScannerIntegrations')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <CustomFieldFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        mode="create"
      />

      <CustomFieldFormDialog
        open={!!editField}
        onOpenChange={(open) => {
          if (!open) setEditField(null);
        }}
        field={editField ?? undefined}
        mode="edit"
      />

      <DeleteCustomFieldDialog
        open={!!deleteField}
        onOpenChange={(open) => {
          if (!open) setDeleteField(null);
        }}
        field={deleteField}
      />
    </div>
  );
}
